import { add, assertAscending, assertStartsAtZero, dataUrl, escapeSVG, subtract } from './_functions';
import { darken, lighten, math, opacify, rgba } from 'polished';

export default $$ => {
  const $ = {
    ...$$,
    colors: {
      gray: $$.gray600,
      grayDark: $$.gray800,
      ...$$.colors,
    },
    themeColors: {
      primary: $$.primaryMain,
      secondary: $$.secondaryMain,
      success: $$.successMain,
      info: $$.infoMain,
      warning: $$.warningMain,
      danger: $$.dangerMain,
      ...$$.themeColors,
    },
  };

  Object.values(setters).forEach(setter => setter($));

  return $;
};

const setters = {};

setters.misc = $ => {
  // Set a specific jump point for requesting color jumps
  $.themeColorInterval = $.themeColorInterval ?? '8%';

  // The yiq lightness value that determines when the lightness of color changes from "dark" to "light". Acceptable values are between 0 and 255.
  $.yiqContrastedThreshold = '150';

  // Customize the light and dark text colors for use in our YIQ color contrast function.
  $.yiqTextDark = $.yiqTextDark ?? $.gray900;
  $.yiqTextLight = $.yiqTextLight ?? $.white;

  // Characters which are escaped by the escape-svg function
  $.escapedCharacters = $.escapedCharacters ?? [
    ['<', '%3c'],
    ['>', '%3e'],
    ['#', '%23'],
    ['(', '%28'],
    [')', '%29'],
  ];
};

setters.options = $ => {
  //
  // Quickly modify global styling by enabling or disabling optional features.
  $.enableCaret = $.enableCaret ?? true;
  $.enableRounded = $.enableRounded ?? true;
  $.enableShadows = $.enableShadows ?? false;
  $.enableGradients = $.enableGradients ?? false;
  $.enableTransitions = $.enableTransitions ?? true;
  $.enablePrefersReducedMotionMediaQuery = $.enablePrefersReducedMotionMediaQuery ?? true;
  $.enableHoverMediaQuery = $.enableHoverMediaQuery ?? false;
  $.enableGridClasses = $.enableGridClasses ?? true;
  $.enablePointerCursorForButtons = $.enablePointerCursorForButtons ?? true;
  $.enablePrintStyles = $.enablePrintStyles ?? true;
  $.enableResponsiveFontSizes = $.enableResponsiveFontSizes ?? false;
  $.enableValidationIcons = $.enableValidationIcons ?? true;
  $.enableDeprecationMessages = $.enableDeprecationMessages ?? true;
};

setters.spacing = $ => {
  //
  // Control the default styling of most Bootstrap elements by modifying these
  // variables. Mostly focused on spacing.
  // You can add more entries to the $spacers map, should you need more variation.

  $.spacer = $.spacer ?? '1rem';
  $.spacers = {
    0: 0,
    1: math(`${$.spacer} * 0.25`),
    2: math(`${$.spacer} * 0.5`),
    3: math(`${$.spacer}`),
    4: math(`${$.spacer} * 1.5`),
    5: math(`${$.spacer} * 3`),
    ...$.spacers,
  };

  // This variable affects the `.h-*` and `.w-*` classes.
  $.sizes = {
    '25': '25%',
    '50': '50%',
    '75': '75%',
    '100': '100%',
    auto: 'auto',
    ...$.sizes,
  };
};

setters.body = $ => {
  //
  // Settings for the `<body>` element.

  $.bodyBg = $.bodyBg ?? $.white;
  $.bodyColor = $.bodyColor ?? $.gray900;
};

setters.links = $ => {
  //
  // Style anchor elements.
  $.linkColor = $.linkColor ?? $.themeColors.primary;
  $.linkDecoration = $.linkDecoration ?? 'none';
  $.linkHoverColor = $.linkHoverColor ?? darken('15%', $.linkColor);
  $.linkHoverDecoration = $.linkHoverDecoration ?? 'underline';
  // Darken percentage for links with `.text-*` class (e.g. `.text-success`)
  $.emphasizedLinkHoverDarkenPercentage = $.emphasizedLinkHoverDarkenPercentage ?? '15%';
};

setters.paragraphs = $ => {
  //
  // Style p element.
  $.paragraphMarginBottom = $.paragraphMarginBottom ?? '1rem';
};

setters.gridBreakpoints = $ => {
  //
  // Define the minimum dimensions at which your layout will change,
  // adapting to different screen sizes, for use in media queries.

  $.gridBreakpoints = $.gridBreakpoints ?? {
    xs: '0',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  };

  assertAscending($.gridBreakpoints, '$grid-breakpoints');
  assertStartsAtZero($.gridBreakpoints, '$grid-breakpoints');

  // Grid containers
  //
  // Define the maximum width of `.container` for different screen sizes.

  $.containerMaxWidths = $.containerMaxWidths ?? {
    sm: '540px',
    md: '720px',
    lg: '960px',
    xl: '1140px',
  };

  assertAscending($.containerMaxWidths, '$container-max-widths');

  // Grid columns
  //
  // Set the number of columns and specify the width of the gutters.

  $.gridColumns = $.gridColumns ?? '12';
  $.gridGutterWidth = $.gridGutterWidth ?? '30px';
  $.gridRowColumns = $.gridRowColumns ?? '6';
};

setters.components = $ => {
  //
  // Define common padding and border radius sizes and more.

  $.lineHeightLg = $.lineHeightLg ?? '1.5';
  $.lineHeightSm = $.lineHeightSm ?? '1.5';

  $.borderWidth = $.borderWidth ?? '1px';
  $.borderColor = $.borderColor ?? '#dee2e6';

  $.borderRadius = $.borderRadius ?? '0.25rem';
  $.borderRadiusLg = $.borderRadiusLg ?? '0.3rem';
  $.borderRadiusSm = $.borderRadiusSm ?? '0.2rem';

  $.roundedPill = $.roundedPill ?? '50rem';

  $.boxShadowSm = $.boxShadowSm ?? `0 0.125rem 0.25rem ${rgba($.black, 0.075)}`;
  $.boxShadow = $.boxShadow ?? `0 0.5rem 1rem ${rgba($.black, 0.15)}`;
  $.boxShadowLg = $.boxShadowLg ?? `0 1rem 3rem ${rgba($.black, 0.175)}`;

  $.componentActiveColor = $.componentActiveColor ?? $.white;
  $.componentActiveBg = $.componentActiveBg ?? $.themeColors.primary;

  $.caretWidth = $.caretWidth ?? '0.3em';
  $.caretVerticalAlign = $.caretVerticalAlign ?? math(`${$.caretWidth} * 0.85`);
  $.caretSpacing = $.caretSpacing ?? math(`${$.caretWidth} * 0.85`);

  $.transitionBase = $.transitionBase ?? 'all 0.2s ease-in-out';
  $.transitionFade = $.transitionFade ?? 'opacity 0.15s linear';
  $.transitionCollapse = $.transitionCollapse ?? 'height 0.35s ease';

  $.embedResponsiveAspectRatios = [[21, 9], [16, 9], [4, 3], [1, 1], ...($.embedResponsiveAspectRatios ?? [])];
};

setters.typography = $ => {
  //
  // Font, line-height, and color for body text, headings, and more.

  // stylelint-disable value-keyword-case
  $.fontFamilySansSerif =
    $.fontFamilySansSerif ??
    [
      '-apple-system',
      'BlinkMacSystemFont',
      "'Segoe UI'",
      'Roboto',
      "'Helvetica Neue'",
      'Arial',
      "'Noto Sans'",
      "'Liberation Sans'",
      'sans-serif',
      "'Apple Color Emoji'",
      "'Segoe UI Emoji'",
      "'Segoe UI Symbol'",
      "'Noto Color Emoji'",
    ].join(', ');
  $.fontFamilyMonospace =
    $.fontFamilyMonospace ?? ['SFMono-Regular', 'Menlo', 'Monaco', 'Consolas', "'Liberation Mono'", "'Courier New'", 'monospace'].join(', ');
  $.fontFamilyBase = $.fontFamilyBase ?? $.fontFamilySansSerif;
  // stylelint-enable value-keyword-case

  $.fontSizeBase = $.fontSizeBase ?? '1rem'; // Assumes the browser default, typically `16px`
  $.fontSizeLg = $.fontSizeLg ?? math(`${$.fontSizeBase} * 1.25`);
  $.fontSizeSm = $.fontSizeSm ?? math(`${$.fontSizeBase} * 0.875`);

  $.fontWeightLighter = $.fontWeightLighter ?? 'lighter';
  $.fontWeightLight = $.fontWeightLight ?? '300';
  $.fontWeightNormal = $.fontWeightNormal ?? '400';
  $.fontWeightBold = $.fontWeightBold ?? '700';
  $.fontWeightBolder = $.fontWeightBolder ?? 'bolder';

  $.fontWeightBase = $.fontWeightBase ?? $.fontWeightNormal;
  $.lineHeightBase = $.lineHeightBase ?? 1.5;

  $.h1FontSize = $.h1FontSize ?? math(`${$.fontSizeBase} * 2.5`);
  $.h2FontSize = $.h2FontSize ?? math(`${$.fontSizeBase} * 2`);
  $.h3FontSize = $.h3FontSize ?? math(`${$.fontSizeBase} * 1.75`);
  $.h4FontSize = $.h4FontSize ?? math(`${$.fontSizeBase} * 1.5`);
  $.h5FontSize = $.h5FontSize ?? math(`${$.fontSizeBase} * 1.25`);
  $.h6FontSize = $.h6FontSize ?? $.fontSizeBase;

  $.headingsMarginBottom = $.headingsMarginBottom ?? math(`${$.spacer} * 0.5`);
  $.headingsFontFamily = $.headingsFontFamily ?? $.fontFamilyBase ?? $.fontFamilySansSerif ?? 'null';
  $.headingsFontWeight = $.headingsFontWeight ?? '500';
  $.headingsLineHeight = $.headingsLineHeight ?? '1.2';
  $.headingsColor = $.headingsColor ?? 'null';

  $.display1Size = $.display1Size ?? '6rem';
  $.display2Size = $.display2Size ?? '5.5rem';
  $.display3Size = $.display3Size ?? '4.5rem';
  $.display4Size = $.display4Size ?? '3.5rem';

  $.display1Weight = $.display1Weight ?? '300';
  $.display2Weight = $.display2Weight ?? '300';
  $.display3Weight = $.display3Weight ?? '300';
  $.display4Weight = $.display4Weight ?? '300';
  $.displayLineHeight = $.displayLineHeight ?? $.headingsLineHeight;

  $.leadFontSize = $.leadFontSize ?? math(`${$.fontSizeBase} * 1.25`);
  $.leadFontWeight = $.leadFontWeight ?? '300';

  $.smallFontSize = $.smallFontSize ?? '80%';

  $.textMuted = $.textMuted ?? $.gray600;

  $.blockquoteSmallColor = $.blockquoteSmallColor ?? $.gray600;
  $.blockquoteSmallFontSize = $.blockquoteSmallFontSize ?? $.smallFontSize;
  $.blockquoteFontSize = $.blockquoteFontSize ?? math(`${$.fontSizeBase} * 1.25`);

  $.hrBorderColor = $.hrBorderColor ?? rgba($.black, 0.1);
  $.hrBorderWidth = $.hrBorderWidth ?? $.borderWidth;

  $.markPadding = $.markPadding ?? '0.2em';

  $.dtFontWeight = $.dtFontWeight ?? $.fontWeightBold;

  $.kbdBoxShadow = $.kbdBoxShadow ?? `inset 0 -0.1rem 0 ${rgba($.black, 0.25)}`;
  $.nestedKbdFontWeight = $.nestedKbdFontWeight ?? $.fontWeightBold;

  $.listInlinePadding = $.listInlinePadding ?? '0.5rem';

  $.markBg = $.markBg ?? '#fcf8e3';

  $.hrMarginY = $.hrMarginY ?? $.spacer;
};

setters.tables = $ => {
  //
  // Customizes the `.table` component with basic values, each used across all table variations.

  $.tableCellPadding = $.tableCellPadding ?? '0.75rem';
  $.tableCellPaddingSm = $.tableCellPaddingSm ?? '0.3rem';

  $.tableColor = $.tableColor ?? $.bodyColor;
  $.tableBg = $.tableBg ?? null;
  $.tableAccentBg = $.tableAccentBg ?? rgba($.black, 0.05);
  $.tableHoverColor = $.tableHoverColor ?? $.tableColor;
  $.tableHoverBg = $.tableHoverBg ?? rgba($.black, 0.075);
  $.tableActiveBg = $.tableActiveBg ?? $.tableHoverBg;

  $.tableBorderWidth = $.tableBorderWidth ?? $.borderWidth;
  $.tableBorderColor = $.tableBorderColor ?? $.borderColor;

  $.tableHeadBg = $.tableHeadBg ?? $.gray200;
  $.tableHeadColor = $.tableHeadColor ?? $.gray700;
  $.tableThFontWeight = $.tableThFontWeight ?? null;

  $.tableDarkColor = $.tableDarkColor ?? $.white;
  $.tableDarkBg = $.tableDarkBg ?? $.gray800;
  $.tableDarkAccentBg = $.tableDarkAccentBg ?? rgba($.white, 0.05);
  $.tableDarkHoverColor = $.tableDarkHoverColor ?? $.tableDarkColor;
  $.tableDarkHoverBg = $.tableDarkHoverBg ?? rgba($.white, 0.075);
  $.tableDarkBorderColor = $.tableDarkBorderColor ?? lighten('7.5%', $.tableDarkBg);

  $.tableStripedOrder = $.tableStripedOrder ?? 'odd';

  $.tableCaptionColor = $.tableCaptionColor ?? $.textMuted;

  $.tableBgLevel = $.tableBgLevel ?? '-9';
  $.tableBorderLevel = $.tableBorderLevel ?? '-6';
};

setters.buttonsForms = $ => {
  //
  // Shared variables that are reassigned to `$input-` and `$btn-` specific variables.

  $.inputBtnPaddingY = $.inputBtnPaddingY ?? '0.375rem';
  $.inputBtnPaddingX = $.inputBtnPaddingX ?? '0.75rem';
  $.inputBtnFontFamily = $.inputBtnFontFamily ?? 'null';
  $.inputBtnFontSize = $.inputBtnFontSize ?? $.fontSizeBase;
  $.inputBtnLineHeight = $.inputBtnLineHeight ?? $.lineHeightBase;

  $.inputBtnFocusWidth = $.inputBtnFocusWidth ?? '0.2rem';
  $.inputBtnFocusColor = $.inputBtnFocusColor ?? rgba($.componentActiveBg, 0.25);
  $.inputBtnFocusBoxShadow = $.inputBtnFocusBoxShadow ?? `0 0 0 ${$.inputBtnFocusWidth} ${$.inputBtnFocusColor}`;

  $.inputBtnPaddingYSm = $.inputBtnPaddingYSm ?? '0.25rem';
  $.inputBtnPaddingXSm = $.inputBtnPaddingXSm ?? '0.5rem';
  $.inputBtnFontSizeSm = $.inputBtnFontSizeSm ?? $.fontSizeSm;
  $.inputBtnLineHeightSm = $.inputBtnLineHeightSm ?? $.lineHeightSm;

  $.inputBtnPaddingYLg = $.inputBtnPaddingYLg ?? '0.5rem';
  $.inputBtnPaddingXLg = $.inputBtnPaddingXLg ?? '1rem';
  $.inputBtnFontSizeLg = $.inputBtnFontSizeLg ?? $.fontSizeLg;
  $.inputBtnLineHeightLg = $.inputBtnLineHeightLg ?? $.lineHeightLg;

  $.inputBtnBorderWidth = $.inputBtnBorderWidth ?? $.borderWidth;
};

setters.buttons = $ => {
  //
  // For each of Bootstrap's buttons, define text, background, and border color.

  $.btnPaddingY = $.btnPaddingY ?? $.inputBtnPaddingY;
  $.btnPaddingX = $.btnPaddingX ?? $.inputBtnPaddingX;
  $.btnFontFamily = $.btnFontFamily ?? $.inputBtnFontFamily;
  $.btnFontSize = $.btnFontSize ?? $.inputBtnFontSize;
  $.btnLineHeight = $.btnLineHeight ?? $.inputBtnLineHeight;
  $.btnWhiteSpace = $.btnWhiteSpace ?? 'null'; // Set to `nowrap` to prevent text wrapping

  $.btnPaddingYSm = $.btnPaddingYSm ?? $.inputBtnPaddingYSm;
  $.btnPaddingXSm = $.btnPaddingXSm ?? $.inputBtnPaddingXSm;
  $.btnFontSizeSm = $.btnFontSizeSm ?? $.inputBtnFontSizeSm;
  $.btnLineHeightSm = $.btnLineHeightSm ?? $.inputBtnLineHeightSm;

  $.btnPaddingYLg = $.btnPaddingYLg ?? $.inputBtnPaddingYLg;
  $.btnPaddingXLg = $.btnPaddingXLg ?? $.inputBtnPaddingXLg;
  $.btnFontSizeLg = $.btnFontSizeLg ?? $.inputBtnFontSizeLg;
  $.btnLineHeightLg = $.btnLineHeightLg ?? $.inputBtnLineHeightLg;

  $.btnBorderWidth = $.btnBorderWidth ?? $.inputBtnBorderWidth;

  $.btnFontWeight = $.btnFontWeight ?? $.fontWeightNormal;
  $.btnBoxShadow = $.btnBoxShadow ?? `inset 0 1px 0 ${rgba($.white, 0.15)}, 0 1px 1px ${rgba($.black, 0.075)}`;
  $.btnFocusWidth = $.btnFocusWidth ?? $.inputBtnFocusWidth;
  $.btnFocusBoxShadow = $.btnFocusBoxShadow ?? $.inputBtnFocusBoxShadow;
  $.btnDisabledOpacity = $.btnDisabledOpacity ?? 0.65;
  $.btnActiveBoxShadow = $.btnActiveBoxShadow ?? `inset 0 3px 5px ${rgba($.black, 0.125)}`;

  $.btnLinkDisabledColor = $.btnLinkDisabledColor ?? $.gray600;

  $.btnBlockSpacingY = $.btnBlockSpacingY ?? '0.5rem';

  // Allows for customizing button radius independently from global border radius
  $.btnBorderRadius = $.btnBorderRadius ?? $.borderRadius;
  $.btnBorderRadiusLg = $.btnBorderRadiusLg ?? $.borderRadiusLg;
  $.btnBorderRadiusSm = $.btnBorderRadiusSm ?? $.borderRadiusSm;

  $.btnTransition =
    $.btnTransition ?? 'color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out';
};

setters.forms = $ => {
  $.labelMarginBottom = $.labelMarginBottom ?? '0.5rem';

  $.inputPaddingY = $.inputPaddingY ?? $.inputBtnPaddingY;
  $.inputPaddingX = $.inputPaddingX ?? $.inputBtnPaddingX;
  $.inputFontFamily = $.inputFontFamily ?? $.inputBtnFontFamily;
  $.inputFontSize = $.inputFontSize ?? $.inputBtnFontSize;
  $.inputFontWeight = $.inputFontWeight ?? $.fontWeightBase;
  $.inputLineHeight = $.inputLineHeight ?? $.inputBtnLineHeight;

  $.inputPaddingYSm = $.inputPaddingYSm ?? $.inputBtnPaddingYSm;
  $.inputPaddingXSm = $.inputPaddingXSm ?? $.inputBtnPaddingXSm;
  $.inputFontSizeSm = $.inputFontSizeSm ?? $.inputBtnFontSizeSm;
  $.inputLineHeightSm = $.inputLineHeightSm ?? $.inputBtnLineHeightSm;

  $.inputPaddingYLg = $.inputPaddingYLg ?? $.inputBtnPaddingYLg;
  $.inputPaddingXLg = $.inputPaddingXLg ?? $.inputBtnPaddingXLg;
  $.inputFontSizeLg = $.inputFontSizeLg ?? $.inputBtnFontSizeLg;
  $.inputLineHeightLg = $.inputLineHeightLg ?? $.inputBtnLineHeightLg;

  $.inputBg = $.inputBg ?? $.white;
  $.inputDisabledBg = $.inputDisabledBg ?? $.gray200;

  $.inputColor = $.inputColor ?? $.gray700;
  $.inputBorderColor = $.inputBorderColor ?? $.gray400;
  $.inputBorderWidth = $.inputBorderWidth ?? $.inputBtnBorderWidth;
  $.inputBoxShadow = $.inputBoxShadow ?? `inset 0 1px 1px ${rgba($.black, 0.075)}`;

  $.inputBorderRadius = $.inputBorderRadius ?? $.borderRadius;
  $.inputBorderRadiusLg = $.inputBorderRadiusLg ?? $.borderRadiusLg;
  $.inputBorderRadiusSm = $.inputBorderRadiusSm ?? $.borderRadiusSm;

  $.inputFocusBg = $.inputFocusBg ?? $.inputBg;
  $.inputFocusBorderColor = $.inputFocusBorderColor ?? lighten('25%', $.componentActiveBg);
  $.inputFocusColor = $.inputFocusColor ?? $.inputColor;
  $.inputFocusWidth = $.inputFocusWidth ?? $.inputBtnFocusWidth;
  $.inputFocusBoxShadow = $.inputFocusBoxShadow ?? $.inputBtnFocusBoxShadow;

  $.inputPlaceholderColor = $.inputPlaceholderColor ?? $.gray600;
  $.inputPlaintextColor = $.inputPlaintextColor ?? $.bodyColor;

  $.inputHeightBorder = $.inputHeightBorder ?? math(`${$.inputBorderWidth} * 2`);

  $.inputHeightInner = $.inputHeightInner ?? add(math(`${$.inputLineHeight} * 1em`), math(`${$.inputPaddingY} * 2`));
  $.inputHeightInnerHalf = $.inputHeightInnerHalf ?? add(math(`${$.inputLineHeight} * 0.5em`), $.inputPaddingY);
  $.inputHeightInnerQuarter = $.inputHeightInnerQuarter ?? add(math(`${$.inputLineHeight} * 0.25em`), math(`${$.inputPaddingY} * 0.5`));

  $.inputHeight =
    $.inputHeight ??
    add(
      math(`${$.inputLineHeight} * 1em`),
      add(
        math(`${$.inputPaddingY} * 2`),
        $.inputHeightBorder
        // false
      )
    );
  $.inputHeightSm =
    $.inputHeightSm ??
    add(
      math(`${$.inputLineHeightSm} * 1em`),
      add(
        math(`${$.inputPaddingYSm} * 2`),
        $.inputHeightBorder
        // false
      )
    );
  $.inputHeightLg =
    $.inputHeightLg ??
    add(
      math(`${$.inputLineHeightLg} * 1em`),
      add(
        math(`${$.inputPaddingYLg} * 2`),
        $.inputHeightBorder
        // false
      )
    );

  $.inputTransition = $.inputTransition ?? 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out';

  $.formTextMarginTop = $.formTextMarginTop ?? '0.25rem';

  $.formCheckInputGutter = $.formCheckInputGutter ?? '1.25rem';
  $.formCheckInputMarginY = $.formCheckInputMarginY ?? '0.3rem';
  $.formCheckInputMarginX = $.formCheckInputMarginX ?? '0.25rem';

  $.formCheckInlineMarginX = $.formCheckInlineMarginX ?? '0.75rem';
  $.formCheckInlineInputMarginX = $.formCheckInlineInputMarginX ?? '0.3125rem';

  $.formGridGutterWidth = $.formGridGutterWidth ?? '10px';
  $.formGroupMarginBottom = $.formGroupMarginBottom ?? '1rem';

  $.inputGroupAddonColor = $.inputGroupAddonColor ?? $.inputColor;
  $.inputGroupAddonBg = $.inputGroupAddonBg ?? $.gray200;
  $.inputGroupAddonBorderColor = $.inputGroupAddonBorderColor ?? $.inputBorderColor;

  $.customFormsTransition =
    $.customFormsTransition ?? 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out';

  $.customControlGutter = $.customControlGutter ?? '0.5rem';
  $.customControlSpacerX = $.customControlSpacerX ?? '1rem';
  $.customControlCursor = $.customControlCursor ?? 'null';

  $.customControlIndicatorSize = $.customControlIndicatorSize ?? '1rem';
  $.customControlIndicatorBg = $.customControlIndicatorBg ?? $.inputBg;

  $.customControlIndicatorBgSize = $.customControlIndicatorBgSize ?? '50% 50%';
  $.customControlIndicatorBoxShadow = $.customControlIndicatorBoxShadow ?? $.inputBoxShadow;
  $.customControlIndicatorBorderColor = $.customControlIndicatorBorderColor ?? $.gray500;
  $.customControlIndicatorBorderWidth = $.customControlIndicatorBorderWidth ?? $.inputBorderWidth;

  $.customControlLabelColor = $.customControlLabelColor ?? 'null';

  $.customControlIndicatorDisabledBg = $.customControlIndicatorDisabledBg ?? $.inputDisabledBg;
  $.customControlLabelDisabledColor = $.customControlLabelDisabledColor ?? $.gray600;

  $.customControlIndicatorCheckedColor = $.customControlIndicatorCheckedColor ?? $.componentActiveColor;
  $.customControlIndicatorCheckedBg = $.customControlIndicatorCheckedBg ?? $.componentActiveBg;
  $.customControlIndicatorCheckedDisabledBg = $.customControlIndicatorCheckedDisabledBg ?? rgba($.themeColors.primary, 0.5);
  $.customControlIndicatorCheckedBoxShadow = $.customControlIndicatorCheckedBoxShadow ?? 'null';
  $.customControlIndicatorCheckedBorderColor = $.customControlIndicatorCheckedBorderColor ?? $.customControlIndicatorCheckedBg;

  $.customControlIndicatorFocusBoxShadow = $.customControlIndicatorFocusBoxShadow ?? $.inputFocusBoxShadow;
  $.customControlIndicatorFocusBorderColor = $.customControlIndicatorFocusBorderColor ?? $.inputFocusBorderColor;

  $.customControlIndicatorActiveColor = $.customControlIndicatorActiveColor ?? $.componentActiveColor;
  $.customControlIndicatorActiveBg = $.customControlIndicatorActiveBg ?? lighten('35%', $.componentActiveBg);
  $.customControlIndicatorActiveBoxShadow = $.customControlIndicatorActiveBoxShadow ?? 'null';
  $.customControlIndicatorActiveBorderColor = $.customControlIndicatorActiveBorderColor ?? $.customControlIndicatorActiveBg;

  $.customCheckboxIndicatorBorderRadius = $.customCheckboxIndicatorBorderRadius ?? $.borderRadius;

  $.customCheckboxIndicatorIconChecked =
    $.customCheckboxIndicatorIconChecked ??
    dataUrl(
      'image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path fill='${$.customControlIndicatorCheckedColor}' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/></svg>`
    );

  $.customCheckboxIndicatorIndeterminateBg = $.customCheckboxIndicatorIndeterminateBg ?? $.componentActiveBg;
  $.customCheckboxIndicatorIndeterminateColor = $.customCheckboxIndicatorIndeterminateColor ?? $.customControlIndicatorCheckedColor;
  $.customCheckboxIndicatorIconIndeterminate =
    $.customCheckboxIndicatorIconIndeterminate ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4'><path stroke='${$.customCheckboxIndicatorIndeterminateColor}' d='M0 2h4'/></svg>`
    );
  $.customCheckboxIndicatorIndeterminateBoxShadow = $.customCheckboxIndicatorIndeterminateBoxShadow ?? 'null';
  $.customCheckboxIndicatorIndeterminateBorderColor = $.customCheckboxIndicatorIndeterminateBorderColor ?? $.customCheckboxIndicatorIndeterminateBg;

  $.customRadioIndicatorBorderRadius = $.customRadioIndicatorBorderRadius ?? '50%';
  $.customRadioIndicatorIconChecked =
    $.customRadioIndicatorIconChecked ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='-4 -4 8 8'><circle r='3' fill='${$.customControlIndicatorCheckedColor}'/></svg>`
    );

  $.customSwitchWidth = $.customSwitchWidth ?? math(`${$.customControlIndicatorSize} * 1.75`);
  $.customSwitchIndicatorBorderRadius = $.customSwitchIndicatorBorderRadius ?? math(`${$.customControlIndicatorSize} * 0.5`);
  $.customSwitchIndicatorSize =
    $.customSwitchIndicatorSize ?? subtract($.customControlIndicatorSize, math(`${$.customControlIndicatorBorderWidth} * 4`));

  $.customSelectPaddingY = $.customSelectPaddingY ?? $.inputPaddingY;
  $.customSelectPaddingX = $.customSelectPaddingX ?? $.inputPaddingX;
  $.customSelectFontFamily = $.customSelectFontFamily ?? $.inputFontFamily;
  $.customSelectFontSize = $.customSelectFontSize ?? $.inputFontSize;
  $.customSelectHeight = $.customSelectHeight ?? $.inputHeight;
  // Extra padding to account for the presence of the background-image based indicator
  $.customSelectIndicatorPadding = $.customSelectIndicatorPadding ?? '1rem';
  $.customSelectFontWeight = $.customSelectFontWeight ?? $.inputFontWeight;
  $.customSelectLineHeight = $.customSelectLineHeight ?? $.inputLineHeight;
  $.customSelectColor = $.customSelectColor ?? $.inputColor;
  $.customSelectDisabledColor = $.customSelectDisabledColor ?? $.gray600;
  $.customSelectBg = $.customSelectBg ?? $.inputBg;
  $.customSelectDisabledBg = $.customSelectDisabledBg ?? $.gray200;
  // In pixels because image dimensions
  $.customSelectBgSize = $.customSelectBgSize ?? '8px 10px';
  $.customSelectIndicatorColor = $.customSelectIndicatorColor ?? $.gray800;
  $.customSelectIndicator =
    $.customSelectIndicator ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'><path fill='${$.customSelectIndicatorColor}' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>`
    );

  // Used so we can have multiple background elements (e.g., arrow and feedback icon)
  $.customSelectBackground =
    $.customSelectBackground ?? `${escapeSVG($.customSelectIndicator)} right ${$.customSelectPaddingX} center / ${$.customSelectBgSizeNoRepeat}`;

  $.customSelectFeedbackIconPaddingRight =
    $.customSelectFeedbackIconPaddingRight ??
    add(math('1em * 0.75'), math(`(2 * ${$.customSelectPaddingY} * 0.75) + ${$.customSelectPaddingX} + ${$.customSelectIndicatorPadding}`));
  $.customSelectFeedbackIconPosition =
    $.customSelectFeedbackIconPosition ?? `center right ${math(`${$.customSelectPaddingX} + ${$.customSelectIndicatorPadding}`)}`;
  $.customSelectFeedbackIconSize = $.customSelectFeedbackIconSize ?? `${$.inputHeightInnerHalf} ${$.inputHeightInnerHalf}`;

  $.customSelectBorderWidth = $.customSelectBorderWidth ?? $.inputBorderWidth;
  $.customSelectBorderColor = $.customSelectBorderColor ?? $.inputBorderColor;
  $.customSelectBorderRadius = $.customSelectBorderRadius ?? $.borderRadius;
  $.customSelectBoxShadow = $.customSelectBoxShadow ?? `inset 0 1px 2px ${rgba($.black, 0.075)}`;

  $.customSelectFocusBorderColor = $.customSelectFocusBorderColor ?? $.inputFocusBorderColor;
  $.customSelectFocusWidth = $.customSelectFocusWidth ?? $.inputFocusWidth;
  $.customSelectFocusBoxShadow = $.customSelectFocusBoxShadow ?? `0 0 0 ${$.customSelectFocusWidth} ${$.inputBtnFocusColor}`;

  $.customSelectPaddingYSm = $.customSelectPaddingYSm ?? $.inputPaddingYSm;
  $.customSelectPaddingXSm = $.customSelectPaddingXSm ?? $.inputPaddingXSm;
  $.customSelectFontSizeSm = $.customSelectFontSizeSm ?? $.inputFontSizeSm;
  $.customSelectHeightSm = $.customSelectHeightSm ?? $.inputHeightSm;

  $.customSelectPaddingYLg = $.customSelectPaddingYLg ?? $.inputPaddingYLg;
  $.customSelectPaddingXLg = $.customSelectPaddingXLg ?? $.inputPaddingXLg;
  $.customSelectFontSizeLg = $.customSelectFontSizeLg ?? $.inputFontSizeLg;
  $.customSelectHeightLg = $.customSelectHeightLg ?? $.inputHeightLg;

  $.customRangeTrackWidth = $.customRangeTrackWidth ?? '100%';
  $.customRangeTrackHeight = $.customRangeTrackHeight ?? '0.5rem';
  $.customRangeTrackCursor = $.customRangeTrackCursor ?? 'pointer';
  $.customRangeTrackBg = $.customRangeTrackBg ?? $.gray100;
  $.customRangeTrackBorderRadius = $.customRangeTrackBorderRadius ?? '1rem';
  $.customRangeTrackBoxShadow = $.customRangeTrackBoxShadow ?? `inset 0 0.25rem 0.25rem ${rgba($.black, 0.1)}`;

  $.customRangeThumbWidth = $.customRangeThumbWidth ?? '1rem';
  $.customRangeThumbHeight = $.customRangeThumbHeight ?? $.customRangeThumbWidth;
  $.customRangeThumbBg = $.customRangeThumbBg ?? $.componentActiveBg;
  $.customRangeThumbBorder = $.customRangeThumbBorder ?? 0;
  $.customRangeThumbBorderRadius = $.customRangeThumbBorderRadius ?? '1rem';
  $.customRangeThumbBoxShadow = $.customRangeThumbBoxShadow ?? `0 0.1rem 0.25rem ${rgba($.black, 0.1)}`;
  $.customRangeThumbFocusBoxShadow = $.customRangeThumbFocusBoxShadow ?? `0 0 0 1px ${$.bodyBg}, $input-focus-box-shadow`;
  // For focus box shadow issue in IE/Edge
  $.customRangeThumbFocusBoxShadowWidth = $.customRangeThumbFocusBoxShadowWidth ?? $.inputFocusWidth;
  $.customRangeThumbActiveBg = $.customRangeThumbActiveBg ?? lighten('35%', $.componentActiveBg);
  $.customRangeThumbDisabledBg = $.customRangeThumbDisabledBg ?? $.gray500;

  $.customFileHeight = $.customFileHeight ?? $.inputHeight;
  $.customFileHeightInner = $.customFileHeightInner ?? $.inputHeightInner;
  $.customFileFocusBorderColor = $.customFileFocusBorderColor ?? $.inputFocusBorderColor;
  $.customFileFocusBoxShadow = $.customFileFocusBoxShadow ?? $.inputFocusBoxShadow;
  $.customFileDisabledBg = $.customFileDisabledBg ?? $.inputDisabledBg;

  $.customFilePaddingY = $.customFilePaddingY ?? $.inputPaddingY;
  $.customFilePaddingX = $.customFilePaddingX ?? $.inputPaddingX;
  $.customFileLineHeight = $.customFileLineHeight ?? $.inputLineHeight;
  $.customFileFontFamily = $.customFileFontFamily ?? $.inputFontFamily;
  $.customFileFontWeight = $.customFileFontWeight ?? $.inputFontWeight;
  $.customFileColor = $.customFileColor ?? $.inputColor;
  $.customFileBg = $.customFileBg ?? $.inputBg;
  $.customFileBorderWidth = $.customFileBorderWidth ?? $.inputBorderWidth;
  $.customFileBorderColor = $.customFileBorderColor ?? $.inputBorderColor;
  $.customFileBorderRadius = $.customFileBorderRadius ?? $.inputBorderRadius;
  $.customFileBoxShadow = $.customFileBoxShadow ?? $.inputBoxShadow;
  $.customFileButtonColor = $.customFileButtonColor ?? $.customFileColor;
  $.customFileButtonBg = $.customFileButtonBg ?? $.inputGroupAddonBg;
  $.customFileText = $.customFileText ?? {
    en: 'Browse',
  };
};

setters.formValidation = $ => {
  $.formFeedbackMarginTop = $.formFeedbackMarginTop ?? $.formTextMarginTop;
  $.formFeedbackFontSize = $.formFeedbackFontSize ?? $.smallFontSize;
  $.formFeedbackValidColor = $.formFeedbackValidColor ?? $.themeColors.success;
  $.formFeedbackInvalidColor = $.formFeedbackInvalidColor ?? $.themeColors.danger;

  $.formFeedbackIconValidColor = $.formFeedbackIconValidColor ?? $.formFeedbackValidColor;
  $.formFeedbackIconValid =
    $.formFeedbackIconValid ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path fill='${$.formFeedbackIconValidColor}' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/></svg>`
    );
  $.formFeedbackIconInvalidColor = $.formFeedbackIconInvalidColor ?? $.formFeedbackInvalidColor;
  $.formFeedbackIconInvalid =
    $.formFeedbackIconInvalid ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='${$.formFeedbackIconInvalidColor}' viewBox='0 0 12 12'><circle cx='6' cy='6' r='4.5'/><path stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/><circle cx='6' cy='8.2' r='.6' fill='${$.formFeedbackIconInvalidColor}' stroke='none'/></svg>`
    );

  $.formValidationStates = {
    valid: {
      color: $.formFeedbackValidColor,
      icon: $.formFeedbackIconValid,
    },
    invalid: {
      color: $.formFeedbackInvalidColor,
      icon: $.formFeedbackIconInvalid,
    },
    ...$.formValidationStates,
  };
};

setters.zIndexMasterList = $ => {
  //
  // Warning: Avoid customizing these values. They're used for a bird's eye view
  // of components dependent on the z-axis and are designed to all work together.

  $.zindexDropdown = $.zindexDropdown ?? 1000;
  $.zindexSticky = $.zindexSticky ?? 1020;
  $.zindexFixed = $.zindexFixed ?? 1030;
  $.zindexModalBackdrop = $.zindexModalBackdrop ?? 1040;
  $.zindexModal = $.zindexModal ?? 1050;
  $.zindexPopover = $.zindexPopover ?? 1060;
  $.zindexTooltip = $.zindexTooltip ?? 1070;
};

setters.navs = $ => {
  $.navLinkPaddingY = $.navLinkPaddingY ?? '0.5rem';
  $.navLinkPaddingX = $.navLinkPaddingX ?? '0.25rem';
  $.navLinkDisabledColor = $.navLinkDisabledColor ?? $.gray600;

  $.navTabsBorderColor = $.navTabsBorderColor ?? $.gray100;
  $.navTabsBorderWidth = $.navTabsBorderWidth ?? $.borderWidth;
  $.navTabsBorderRadius = $.navTabsBorderRadius ?? $.borderRadius;
  $.navTabsLinkHoverBorderColor = $.navTabsLinkHoverBorderColor ?? `${$.gray200} ${$.gray200} ${$.navTabsBorderColor}`;
  $.navTabsLinkActiveColor = $.navTabsLinkActiveColor ?? $.gray700;
  $.navTabsLinkActiveBg = $.navTabsLinkActiveBg ?? $.bodyBg;
  $.navTabsLinkActiveBorderColor = $.navTabsLinkActiveBorderColor ?? `${$.gray100} ${$.gray100} ${$.navTabsLinkActiveBg}`;

  $.navPillsBorderRadius = $.navPillsBorderRadius ?? $.borderRadius;
  $.navPillsLinkActiveColor = $.navPillsLinkActiveColor ?? $.componentActiveColor;
  $.navPillsLinkActiveBg = $.navPillsLinkActiveBg ?? $.componentActiveBg;

  $.navDividerColor = $.navDividerColor ?? $.gray200;
  $.navDividerMarginY = $.navDividerMarginY ?? math(`${$.spacer} * 0.5`);
};

setters.navbar = $ => {
  $.navbarPaddingY = $.navbarPaddingY ?? math(`${$.spacer} * 0.5`);
  $.navbarPaddingX = $.navbarPaddingX ?? $.spacer;

  $.navbarNavLinkPaddingX = $.navbarNavLinkPaddingX ?? '0.5rem';

  $.navbarBrandFontSize = $.navbarBrandFontSize ?? $.fontSizeLg;
  // Compute the navbar-brand padding-y so the navbar-brand will have the same height as navbar-text and nav-link
  $.navLinkHeight = $.navLinkHeight ?? math(`${$.fontSizeBase} * ${$.lineHeightBase} + ${$.navLinkPaddingY} * 2`);
  $.navbarBrandHeight = $.navbarBrandHeight ?? math(`${$.navbarBrandFontSize} * ${$.lineHeightBase}`);
  $.navbarBrandPaddingY = $.navbarBrandPaddingY ?? math(`(${$.navLinkHeight} - ${$.navbarBrandHeight}) * 0.5`);

  $.navbarTogglerPaddingY = $.navbarTogglerPaddingY ?? '0.25rem';
  $.navbarTogglerPaddingX = $.navbarTogglerPaddingX ?? '0.75rem';
  $.navbarTogglerFontSize = $.navbarTogglerFontSize ?? $.fontSizeLg;
  $.navbarTogglerBorderRadius = $.navbarTogglerBorderRadius ?? $.btnBorderRadius;

  $.navbarNavScrollMaxHeight = $.navbarNavScrollMaxHeight ?? '75vh';

  $.navbarDarkColor = $.navbarDarkColor ?? rgba($.white, 0.5);
  $.navbarDarkHoverColor = $.navbarDarkHoverColor ?? rgba($.white, 0.75);
  $.navbarDarkActiveColor = $.navbarDarkActiveColor ?? $.white;
  $.navbarDarkDisabledColor = $.navbarDarkDisabledColor ?? rgba($.white, 0.25);
  $.navbarDarkTogglerIconBg =
    $.navbarDarkTogglerIconBg ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'><path stroke='${$.navbarDarkColor}' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/></svg>`
    );
  $.navbarDarkTogglerBorderColor = $.navbarDarkTogglerBorderColor ?? rgba($.white, 0.1);

  $.navbarLightColor = $.navbarLightColor ?? rgba($.black, 0.5);
  $.navbarLightHoverColor = $.navbarLightHoverColor ?? rgba($.black, 0.7);
  $.navbarLightActiveColor = $.navbarLightActiveColor ?? rgba($.black, 0.9);
  $.navbarLightDisabledColor = $.navbarLightDisabledColor ?? rgba($.black, 0.3);
  $.navbarLightTogglerIconBg =
    $.navbarLightTogglerIconBg ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'><path stroke='${$.navbarLightColor}' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/></svg>`
    );
  $.navbarLightTogglerBorderColor = $.navbarLightTogglerBorderColor ?? rgba($.black, 0.1);

  $.navbarLightBrandColor = $.navbarLightBrandColor ?? $.navbarLightActiveColor;
  $.navbarLightBrandHoverColor = $.navbarLightBrandHoverColor ?? $.navbarLightActiveColor;
  $.navbarDarkBrandColor = $.navbarDarkBrandColor ?? $.navbarDarkActiveColor;
  $.navbarDarkBrandHoverColor = $.navbarDarkBrandHoverColor ?? $.navbarDarkActiveColor;
};

setters.dropdowns = $ => {
  //
  // Dropdown menu container and contents.

  $.dropdownMinWidth = $.dropdownMinWidth ?? '10rem';
  $.dropdownPaddingX = $.dropdownPaddingX ?? '0';
  $.dropdownPaddingY = $.dropdownPaddingY ?? '0.5rem';
  $.dropdownSpacer = $.dropdownSpacer ?? '0.125rem';
  $.dropdownFontSize = $.dropdownFontSize ?? $.fontSizeBase;
  $.dropdownColor = $.dropdownColor ?? $.bodyColor;
  $.dropdownBg = $.dropdownBg ?? $.white;
  $.dropdownBorderColor = $.dropdownBorderColor ?? rgba($.black, 0.15);
  $.dropdownBorderRadius = $.dropdownBorderRadius ?? $.borderRadius;
  $.dropdownBorderWidth = $.dropdownBorderWidth ?? $.borderWidth;
  $.dropdownInnerBorderRadius = $.dropdownInnerBorderRadius ?? subtract($.dropdownBorderRadius, $.dropdownBorderWidth);
  $.dropdownDividerBg = $.dropdownDividerBg ?? $.gray200;
  $.dropdownDividerMarginY = $.dropdownDividerMarginY ?? $.navDividerMarginY;
  $.dropdownBoxShadow = $.dropdownBoxShadow ?? `0 0.5rem 1rem ${rgba($.black, 0.175)}`;

  $.dropdownLinkColor = $.dropdownLinkColor ?? $.gray900;
  $.dropdownLinkHoverColor = $.dropdownLinkHoverColor ?? darken('5%', $.gray900);
  $.dropdownLinkHoverBg = $.dropdownLinkHoverBg ?? $.gray200;

  $.dropdownLinkActiveColor = $.dropdownLinkActiveColor ?? $.componentActiveColor;
  $.dropdownLinkActiveBg = $.dropdownLinkActiveBg ?? $.componentActiveBg;

  $.dropdownLinkDisabledColor = $.dropdownLinkDisabledColor ?? $.gray500;

  $.dropdownItemPaddingY = $.dropdownItemPaddingY ?? '0.25rem';
  $.dropdownItemPaddingX = $.dropdownItemPaddingX ?? '1.5rem';

  $.dropdownHeaderColor = $.dropdownHeaderColor ?? $.gray600;
  $.dropdownHeaderPadding = $.dropdownHeaderPadding ?? `${$.dropdownPaddingY} ${$.dropdownItemPaddingX}`;
};

setters.pagination = $ => {
  $.paginationPaddingY = $.paginationPaddingY ?? '0.5rem';
  $.paginationPaddingX = $.paginationPaddingX ?? '0.75rem';
  $.paginationPaddingYSm = $.paginationPaddingYSm ?? '0.25rem';
  $.paginationPaddingXSm = $.paginationPaddingXSm ?? '0.5rem';
  $.paginationPaddingYLg = $.paginationPaddingYLg ?? '0.75rem';
  $.paginationPaddingXLg = $.paginationPaddingXLg ?? '1.5rem';
  $.paginationLineHeight = $.paginationLineHeight ?? '1.25';

  $.paginationColor = $.paginationColor ?? $.linkColor;
  $.paginationBg = $.paginationBg ?? $.white;
  $.paginationBorderWidth = $.paginationBorderWidth ?? $.borderWidth;
  $.paginationBorderColor = $.paginationBorderColor ?? $.gray100;

  $.paginationFocusBoxShadow = $.paginationFocusBoxShadow ?? $.inputBtnFocusBoxShadow;
  $.paginationFocusOutline = $.paginationFocusOutline ?? 0;

  $.paginationHoverColor = $.paginationHoverColor ?? $.linkHoverColor;
  $.paginationHoverBg = $.paginationHoverBg ?? $.gray200;
  $.paginationHoverBorderColor = $.paginationHoverBorderColor ?? $.gray100;

  $.paginationActiveColor = $.paginationActiveColor ?? $.componentActiveColor;
  $.paginationActiveBg = $.paginationActiveBg ?? $.componentActiveBg;
  $.paginationActiveBorderColor = $.paginationActiveBorderColor ?? $.paginationActiveBg;

  $.paginationDisabledColor = $.paginationDisabledColor ?? $.gray600;
  $.paginationDisabledBg = $.paginationDisabledBg ?? $.white;
  $.paginationDisabledBorderColor = $.paginationDisabledBorderColor ?? $.gray100;

  $.paginationBorderRadiusSm = $.paginationBorderRadiusSm ?? $.borderRadiusSm;
  $.paginationBorderRadiusLg = $.paginationBorderRadiusLg ?? $.borderRadiusLg;
};

setters.jumbotron = $ => {
  $.jumbotronPadding = $.jumbotronPadding ?? '2rem';
  $.jumbotronColor = $.jumbotronColor ?? 'null';
  $.jumbotronBg = $.jumbotronBg ?? $.gray200;
};

setters.cards = $ => {
  $.cardSpacerY = $.cardSpacerY ?? '0.75rem';
  $.cardSpacerX = $.cardSpacerX ?? '1.25rem';
  $.cardBorderWidth = $.cardBorderWidth ?? $.borderWidth;
  $.cardBorderRadius = $.cardBorderRadius ?? $.borderRadius;
  $.cardBorderColor = $.cardBorderColor ?? rgba($.black, 0.125);
  $.cardInnerBorderRadius = $.cardInnerBorderRadius ?? subtract($.cardBorderRadius, $.cardBorderWidth);
  $.cardCapBg = $.cardCapBg ?? rgba($.black, 0.03);
  $.cardCapColor = $.cardCapColor ?? 'null';
  $.cardHeight = $.cardHeight ?? 'null';
  $.cardColor = $.cardColor ?? 'null';
  $.cardBg = $.cardBg ?? $.white;

  $.cardImgOverlayPadding = $.cardImgOverlayPadding ?? '1.25rem';

  $.cardGroupMargin = $.cardGroupMargin ?? math(`${$.gridGutterWidth} * 0.5`);
  $.cardDeckMargin = $.cardDeckMargin ?? $.cardGroupMargin;

  $.cardColumnsCount = $.cardColumnsCount ?? 3;
  $.cardColumnsGap = $.cardColumnsGap ?? '1.25rem';
  $.cardColumnsMargin = $.cardColumnsMargin ?? $.cardSpacerY;
};

setters.tooltips = $ => {
  $.tooltipFontSize = $.tooltipFontSize ?? $.fontSizeSm;
  $.tooltipMaxWidth = $.tooltipMaxWidth ?? '200px';
  $.tooltipColor = $.white;
  $.tooltipBg = $.gray800;
  $.tooltipBorderRadius = $.tooltipBorderRadius ?? $.borderRadius;
  $.tooltipOpacity = $.tooltipOpacity ?? 0.9;
  $.tooltipPaddingY = $.tooltipPaddingY ?? '0.25rem';
  $.tooltipPaddingX = $.tooltipPaddingX ?? '0.5rem';
  $.tooltipMargin = $.tooltipMargin ?? 0;

  $.tooltipArrowWidth = $.tooltipArrowWidth ?? '0.8rem';
  $.tooltipArrowHeight = $.tooltipArrowHeight ?? '0.4rem';
  $.tooltipArrowColor = $.tooltipArrowColor ?? $.tooltipBg;

  // Form tooltips must come after regular tooltips
  $.formFeedbackTooltipPaddingY = $.formFeedbackTooltipPaddingY ?? $.tooltipPaddingY;
  $.formFeedbackTooltipPaddingX = $.formFeedbackTooltipPaddingX ?? $.tooltipPaddingX;
  $.formFeedbackTooltipFontSize = $.formFeedbackTooltipFontSize ?? $.tooltipFontSize;
  $.formFeedbackTooltipLineHeight = $.formFeedbackTooltipLineHeight ?? $.lineHeightBase;
  $.formFeedbackTooltipOpacity = $.formFeedbackTooltipOpacity ?? $.tooltipOpacity;
  $.formFeedbackTooltipBorderRadius = $.formFeedbackTooltipBorderRadius ?? $.tooltipBorderRadius;
};

setters.popovers = $ => {
  $.popoverFontSize = $.popoverFontSize ?? $.fontSizeSm;
  $.popoverBg = $.popoverBg ?? $.white;
  $.popoverMaxWidth = $.popoverMaxWidth ?? '276px';
  $.popoverBorderWidth = $.popoverBorderWidth ?? $.borderWidth;
  $.popoverBorderColor = $.popoverBorderColor ?? rgba($.black, 0.2);
  $.popoverBorderRadius = $.popoverBorderRadius ?? $.borderRadiusLg;
  $.popoverInnerBorderRadius = $.popoverInnerBorderRadius ?? subtract($.popoverBorderRadius, $.popoverBorderWidth);
  $.popoverBoxShadow = $.popoverBoxShadow ?? `0 0.25rem 0.5rem ${rgba($.black, 0.2)}`;

  $.popoverHeaderBg = $.popoverHeaderBg ?? darken('3%', $.popoverBg);
  $.popoverHeaderColor = $.popoverHeaderColor ?? $.headingsColor;
  $.popoverHeaderPaddingY = $.popoverHeaderPaddingY ?? '0.5rem';
  $.popoverHeaderPaddingX = $.popoverHeaderPaddingX ?? '0.75rem';

  $.popoverBodyColor = $.popoverBodyColor ?? $.bodyColor;
  $.popoverBodyPaddingY = $.popoverBodyPaddingY ?? $.popoverHeaderPaddingY;
  $.popoverBodyPaddingX = $.popoverBodyPaddingX ?? $.popoverHeaderPaddingX;

  $.popoverArrowWidth = $.popoverArrowWidth ?? '1rem';
  $.popoverArrowHeight = $.popoverArrowHeight ?? '0.5rem';
  $.popoverArrowColor = $.popoverArrowColor ?? $.popoverBg;

  $.popoverArrowOuterColor = $.popoverArrowOuterColor ?? opacify(0.05, $.popoverBorderColor);
};

setters.toasts = $ => {
  $.toastMaxWidth = $.toastMaxWidth ?? '350px';
  $.toastPaddingX = $.toastPaddingX ?? '0.75rem';
  $.toastPaddingY = $.toastPaddingY ?? '0.25rem';
  $.toastFontSize = $.toastFontSize ?? '0.875rem';
  $.toastColor = $.toastColor ?? 'null';
  $.toastBackgroundColor = $.toastBackgroundColor ?? rgba($.white, 0.85);
  $.toastBorderWidth = $.toastBorderWidth ?? '1px';
  $.toastBorderColor = $.toastBorderColor ?? rgba(0, 0, 0, 0.1);
  $.toastBorderRadius = $.toastBorderRadius ?? '0.25rem';
  $.toastBoxShadow = $.toastBoxShadow ?? `0 0.25rem 0.75rem ${rgba($.black, 0.1)}`;

  $.toastHeaderColor = $.toastHeaderColor ?? $.gray600;
  $.toastHeaderBackgroundColor = $.toastHeaderBackgroundColor ?? rgba($.white, 0.85);
  $.toastHeaderBorderColor = $.toastHeaderBorderColor ?? rgba(0, 0, 0, 0.05);
};

setters.badges = $ => {
  $.badgeFontSize = $.badgeFontSize ?? '75%';
  $.badgeFontWeight = $.badgeFontWeight ?? $.fontWeightBold;
  $.badgePaddingY = $.badgePaddingY ?? '0.25em';
  $.badgePaddingX = $.badgePaddingX ?? '0.4em';
  $.badgeBorderRadius = $.badgeBorderRadius ?? $.borderRadius;

  $.badgeTransition = $.badgeTransition ?? $.btnTransition;
  $.badgeFocusWidth = $.badgeFocusWidth ?? $.inputBtnFocusWidth;

  $.badgePillPaddingX = $.badgePillPaddingX ?? '0.6em';
  // Use a higher than normal value to ensure completely rounded edges when
  // customizing padding or font-size on labels.
  $.badgePillBorderRadius = $.badgePillBorderRadius ?? '10rem';
};

setters.modals = $ => {
  // Padding applied to the modal body
  $.modalInnerPadding = $.modalInnerPadding ?? '1rem';

  // Margin between elements in footer, must be lower than or equal to 2 * $modal-inner-padding
  $.modalFooterMarginBetween = $.modalFooterMarginBetween ?? '0.5rem';

  $.modalDialogMargin = $.modalDialogMargin ?? '0.5rem';
  $.modalDialogMarginYSmUp = $.modalDialogMarginYSmUp ?? '1.75rem';

  $.modalTitleLineHeight = $.modalTitleLineHeight ?? $.lineHeightBase;

  $.modalContentColor = $.modalContentColor ?? 'null';
  $.modalContentBg = $.modalContentBg ?? $.white;
  $.modalContentBorderColor = $.modalContentBorderColor ?? rgba($.black, 0.2);
  $.modalContentBorderWidth = $.modalContentBorderWidth ?? $.borderWidth;
  $.modalContentBorderRadius = $.modalContentBorderRadius ?? $.borderRadiusLg;
  $.modalContentInnerBorderRadius = $.modalContentInnerBorderRadius ?? subtract($.modalContentBorderRadius, $.modalContentBorderWidth);
  $.modalContentBoxShadowXs = $.modalContentBoxShadowXs ?? `0 0.25rem 0.5rem ${rgba($.black, 0.5)}`;
  $.modalContentBoxShadowSmUp = $.modalContentBoxShadowSmUp ?? `0 0.5rem 1rem ${rgba($.black, 0.5)}`;

  $.modalBackdropBg = $.modalBackdropBg ?? $.black;
  $.modalBackdropOpacity = $.modalBackdropOpacity ?? 0.5;
  $.modalHeaderBorderColor = $.modalHeaderBorderColor ?? $.borderColor;
  $.modalFooterBorderColor = $.modalFooterBorderColor ?? $.modalHeaderBorderColor;
  $.modalHeaderBorderWidth = $.modalHeaderBorderWidth ?? $.modalContentBorderWidth;
  $.modalFooterBorderWidth = $.modalFooterBorderWidth ?? $.modalHeaderBorderWidth;
  $.modalHeaderPaddingY = $.modalHeaderPaddingY ?? '1rem';
  $.modalHeaderPaddingX = $.modalHeaderPaddingX ?? '1rem';
  $.modalHeaderPadding = $.modalHeaderPadding ?? `${$.modalHeaderPaddingY} ${$.modalHeaderPaddingX}`; // Keep this for backwards compatibility

  $.modalXl = $.modalXl ?? '1140px';
  $.modalLg = $.modalLg ?? '800px';
  $.modalMd = $.modalMd ?? '500px';
  $.modalSm = $.modalSm ?? '300px';

  $.modalFadeTransform = $.modalFadeTransform ?? 'translate(0, -50px)';
  $.modalShowTransform = $.modalShowTransform ?? 'none';
  $.modalTransition = $.modalTransition ?? 'transform 0.3s ease-out';
  $.modalScaleTransform = $.modalScaleTransform ?? 'scale(1.02)';
};

setters.alerts = $ => {
  //
  // Define alert colors, border radius, and padding.

  $.alertPaddingY = $.alertPaddingY ?? '0.75rem';
  $.alertPaddingX = $.alertPaddingX ?? '1.25rem';
  $.alertMarginBottom = $.alertMarginBottom ?? '1rem';
  $.alertBorderRadius = $.alertBorderRadius ?? $.borderRadius;
  $.alertLinkFontWeight = $.alertLinkFontWeight ?? $.fontWeightBold;
  $.alertBorderWidth = $.alertBorderWidth ?? $.borderWidth;

  $.alertBgLevel = $.alertBgLevel ?? -10;
  $.alertBorderLevel = $.alertBorderLevel ?? -9;
  $.alertColorLevel = $.alertColorLevel ?? 6;
};

setters.progressBars = $ => {
  $.progressHeight = $.progressHeight ?? '1rem';
  $.progressFontSize = $.progressFontSize ?? math(`${$.fontSizeBase} * 0.75`);
  $.progressBg = $.progressBg ?? $.gray200;
  $.progressBorderRadius = $.progressBorderRadius ?? $.borderRadius;
  $.progressBoxShadow = $.progressBoxShadow ?? `inset 0 0.1rem 0.1rem ${rgba($.black, 0.1)}`;
  $.progressBarColor = $.progressBarColor ?? $.white;
  $.progressBarBg = $.progressBarBg ?? $.themeColors.primary;
  $.progressBarAnimationTiming = $.progressBarAnimationTiming ?? '1s linear infinite';
  $.progressBarTransition = $.progressBarTransition ?? 'width 0.6s ease';
};

setters.listGroup = $ => {
  $.listGroupColor = $.listGroupColor ?? 'null';
  $.listGroupBg = $.listGroupBg ?? $.white;
  $.listGroupBorderColor = $.listGroupBorderColor ?? rgba($.black, 0.125);
  $.listGroupBorderWidth = $.listGroupBorderWidth ?? $.borderWidth;
  $.listGroupBorderRadius = $.listGroupBorderRadius ?? $.borderRadius;

  $.listGroupItemPaddingY = $.listGroupItemPaddingY ?? '0.75rem';
  $.listGroupItemPaddingX = $.listGroupItemPaddingX ?? '1.25rem';

  $.listGroupHoverBg = $.listGroupHoverBg ?? $.gray100;
  $.listGroupActiveColor = $.listGroupActiveColor ?? $.componentActiveColor;
  $.listGroupActiveBg = $.listGroupActiveBg ?? $.componentActiveBg;
  $.listGroupActiveBorderColor = $.listGroupActiveBorderColor ?? $.listGroupActiveBg;

  $.listGroupDisabledColor = $.listGroupDisabledColor ?? $.gray600;
  $.listGroupDisabledBg = $.listGroupDisabledBg ?? $.listGroupBg;

  $.listGroupActionColor = $.listGroupActionColor ?? $.gray700;
  $.listGroupActionHoverColor = $.listGroupActionHoverColor ?? $.listGroupActionColor;

  $.listGroupActionActiveColor = $.listGroupActionActiveColor ?? $.bodyColor;
  $.listGroupActionActiveBg = $.listGroupActionActiveBg ?? $.gray200;
};

setters.imageThumbnails = $ => {
  $.thumbnailPadding = $.thumbnailPadding ?? '0.25rem';
  $.thumbnailBg = $.thumbnailBg ?? $.bodyBg;
  $.thumbnailBorderWidth = $.thumbnailBorderWidth ?? $.borderWidth;
  $.thumbnailBorderColor = $.thumbnailBorderColor ?? $.gray100;
  $.thumbnailBorderRadius = $.thumbnailBorderRadius ?? $.borderRadius;
  $.thumbnailBoxShadow = $.thumbnailBoxShadow ?? `0 1px 2px ${rgba($.black, 0.075)}`;
};

setters.figures = $ => {
  $.figureCaptionFontSize = $.figureCaptionFontSize ?? '90%';
  $.figureCaptionColor = $.figureCaptionColor ?? $.gray600;
};

setters.breadcrumbs = $ => {
  $.breadcrumbFontSize = $.breadcrumbFontSize ?? 'null';

  $.breadcrumbPaddingY = $.breadcrumbPaddingY ?? '0.75rem';
  $.breadcrumbPaddingX = $.breadcrumbPaddingX ?? '1rem';
  $.breadcrumbItemPadding = $.breadcrumbItemPadding ?? '0.5rem';

  $.breadcrumbMarginBottom = $.breadcrumbMarginBottom ?? '1rem';

  $.breadcrumbBg = $.breadcrumbBg ?? $.gray200;
  $.breadcrumbDividerColor = $.breadcrumbDividerColor ?? $.gray600;
  $.breadcrumbActiveColor = $.breadcrumbActiveColor ?? $.gray600;
  $.breadcrumbDivider = $.breadcrumbDivider ?? '/';

  $.breadcrumbBorderRadius = $.breadcrumbBorderRadius ?? $.borderRadius;
};

setters.carousel = $ => {
  $.carouselControlColor = $.carouselControlColor ?? $.white;
  $.carouselControlWidth = $.carouselControlWidth ?? '15%';
  $.carouselControlOpacity = $.carouselControlOpacity ?? '0.5';
  $.carouselControlHoverOpacity = $.carouselControlHoverOpacity ?? '0.9';
  $.carouselControlTransition = $.carouselControlTransition ?? 'opacity 0.15s ease';

  $.carouselIndicatorWidth = $.carouselIndicatorWidth ?? '30px';
  $.carouselIndicatorHeight = $.carouselIndicatorHeight ?? '3px';
  $.carouselIndicatorHitAreaHeight = $.carouselIndicatorHitAreaHeight ?? '10px';
  $.carouselIndicatorSpacer = $.carouselIndicatorSpacer ?? '3px';
  $.carouselIndicatorActiveBg = $.carouselIndicatorActiveBg ?? $.white;
  $.carouselIndicatorTransition = $.carouselIndicatorTransition ?? 'opacity 0.6s ease';

  $.carouselCaptionWidth = $.carouselCaptionWidth ?? '70%';
  $.carouselCaptionColor = $.carouselCaptionColor ?? $.white;

  $.carouselControlIconWidth = $.carouselControlIconWidth ?? '20px';

  $.carouselControlPrevIconBg =
    $.carouselControlPrevIconBg ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' fill='${$.carouselControlColor}' width='8' height='8' viewBox='0 0 8 8'><path d='M5.25 0l-4 4 4 4 1.5-1.5L4.25 4l2.5-2.5L5.25 0z'/></svg>`
    );
  $.carouselControlNextIconBg =
    $.carouselControlNextIconBg ??
    dataUrl(
      'data:image/svg+xml',
      `<svg xmlns='http://www.w3.org/2000/svg' fill='${$.carouselControlColor}' width='8' height='8' viewBox='0 0 8 8'><path d='M2.75 0l-1.5 1.5L3.75 4l-2.5 2.5L2.75 8l4-4-4-4z'/></svg>`
    );

  $.carouselTransitionDuration = $.carouselTransitionDuration ?? '0.6s';
  // Define transform transition first if using multiple transitions (e.g., `transform 2s ease, opacity .5s ease-out`)
  $.carouselTransition = $.carouselTransition ?? 'transform $carousel-transition-duration ease-in-out';
};

setters.spinners = $ => {
  $.spinnerWidth = $.spinnerWidth ?? '2rem';
  $.spinnerHeight = $.spinnerHeight ?? $.spinnerWidth;
  $.spinnerVerticalAlign = $.spinnerVerticalAlign ?? '-0.125em';
  $.spinnerBorderWidth = $.spinnerBorderWidth ?? '0.25em';

  $.spinnerWidthSm = $.spinnerWidthSm ?? '1rem';
  $.spinnerHeightSm = $.spinnerHeightSm ?? $.spinnerWidthSm;
  $.spinnerBorderWidthSm = $.spinnerBorderWidthSm ?? '0.2em';
};

setters.close = $ => {
  $.closeFontSize = $.closeFontSize ?? math(`${$.fontSizeBase} * 1.5`);
  $.closeFontWeight = $.closeFontWeight ?? $.fontWeightBold;
  $.closeColor = $.closeColor ?? $.black;
  $.closeTextShadow = $.closeTextShadow ?? `0 1px 0 ${$.white}`;
};

setters.code = $ => {
  $.codeFontSize = $.codeFontSize ?? '87.5%';
  $.codeColor = $.codeColor ?? $.pink;

  $.kbdPaddingY = $.kbdPaddingY ?? '0.2rem';
  $.kbdPaddingX = $.kbdPaddingX ?? '0.4rem';
  $.kbdFontSize = $.kbdFontSize ?? $.codeFontSize;
  $.kbdColor = $.kbdColor ?? $.white;
  $.kbdBg = $.kbdBg ?? $.gray900;

  $.preColor = $.preColor ?? $.gray900;
  $.preScrollableMaxHeight = $.preScrollableMaxHeight ?? '340px';
};

setters.utilities = $ => {
  // Utilities
  $.displays = $.displays ?? ['none', 'inline', 'inline-block', 'block', 'table', 'table-row', 'table-cell', 'flex', 'inline-flex'];
  $.overflows = $.overflows ?? ['auto', 'hidden'];
  $.positions = $.positions ?? ['static', 'relative', 'absolute', 'fixed', 'sticky'];
  $.userSelects = $.userSelects ?? ['all', 'auto', 'none'];

  // Printing
  $.printPageSize = $.printPageSize ?? 'a3';
  $.printBodyMinWidth = $.printBodyMinWidth ?? $.gridBreakpoints.lg;
};
